import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Chip, CircularProgress, Divider, Drawer, IconButton, InputAdornment, Paper, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { workspace } from '../theme';
import { getActiveProjectId, subscribeProjectChange } from '../services/projectContext';
import { askCopilot, fetchSuggestions, sendFeedback, CopilotAnswer, CopilotScope, CopilotTurn } from './copilotClient';

const PANEL_WIDTH = 420;
// The global AppBar is fixed at 64px with a higher z-index, so the drawer starts below it.
const APP_BAR_HEIGHT = 64;
const MAX_VISIBLE_EXCHANGES = 20;
const STICK_TO_BOTTOM_PX = 80;

type Message =
  | { kind: 'user'; id: string; text: string }
  | { kind: 'assistant'; id: string; answer: CopilotAnswer }
  | { kind: 'error'; id: string; text: string };

const cap = (list: Message[]) =>
  (list.length > MAX_VISIBLE_EXCHANGES * 2 ? list.slice(-MAX_VISIBLE_EXCHANGES * 2) : list);

const CITATION_ICON = {
  consent: <DescriptionOutlinedIcon fontSize="small" />,
  article: <ArticleOutlinedIcon fontSize="small" />,
  source: <StorageOutlinedIcon fontSize="small" />,
};

type Props = {
  open: boolean;
  seedQuestion: string | null;
  onClose: () => void;
  onSeedConsumed: () => void;
};

export default function CopilotPanel({ open, seedQuestion, onClose, onSeedConsumed }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [projectId, setProjectId] = useState<number | null>(getActiveProjectId());
  const [messages, setMessages] = useState<Message[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [scope, setScope] = useState<CopilotScope>({});
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [rated, setRated] = useState<Record<string, string>>({});
  const [conversationKey, setConversationKey] = useState(0);
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);
  // Answers that arrive after a reset belong to a conversation that no longer exists.
  const conversationRef = useRef(0);

  const resetConversation = useCallback(() => {
    conversationRef.current += 1;
    setMessages([]);
    setRated({});
    setQuestion('');
    setBusy(false);
    stickToBottom.current = true;
    setConversationKey((value) => value + 1);
  }, []);

  useEffect(() => subscribeProjectChange(() => {
    setProjectId(getActiveProjectId());
    setScope({});
    setSuggestions([]);
    resetConversation();
  }), [resetConversation]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    fetchSuggestions(projectId, location.pathname)
      .then((payload) => {
        if (!active) return;
        setSuggestions(payload.suggestions || []);
        setScope(payload.scope || {});
      })
      .catch(() => { if (active) setSuggestions([]); });
    return () => { active = false; };
  }, [open, projectId, location.pathname, conversationKey]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, conversationKey]);

  useEffect(() => {
    if (stickToBottom.current) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, busy]);

  const handleScroll = () => {
    const node = scrollRef.current;
    if (!node) return;
    stickToBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < STICK_TO_BOTTOM_PX;
  };

  const submit = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    const token = conversationRef.current;
    const history: CopilotTurn[] = messages.map((message) => (
      message.kind === 'user'
        ? { role: 'user' as const, content: message.text }
        : { role: 'assistant' as const, content: message.kind === 'assistant' ? message.answer.summary : message.text }
    ));
    stickToBottom.current = true;
    setMessages((current) => cap([...current, { kind: 'user', id: `u-${Date.now()}`, text: trimmed }]));
    setQuestion('');
    setBusy(true);
    try {
      const answer = await askCopilot(trimmed, projectId, history);
      if (conversationRef.current !== token) return;
      setScope(answer.scope || {});
      setMessages((current) => cap([...current, { kind: 'assistant', id: answer.answer_id, answer }]));
    } catch (error: any) {
      if (conversationRef.current !== token) return;
      setMessages((current) => cap([...current, {
        kind: 'error', id: `e-${Date.now()}`,
        text: error?.message || 'The Copilot could not be reached.',
      }]));
    } finally {
      if (conversationRef.current === token) setBusy(false);
    }
  }, [busy, messages, projectId]);

  useEffect(() => {
    if (open && seedQuestion) {
      onSeedConsumed();
      submit(seedQuestion);
    }
  }, [open, seedQuestion, onSeedConsumed, submit]);

  const rate = (answerId: string, rating: 'helpful' | 'unhelpful') => {
    setRated((current) => ({ ...current, [answerId]: rating }));
    sendFeedback(answerId, rating, projectId);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': 'copilot-title',
        sx: {
          width: { xs: '100%', sm: PANEL_WIDTH },
          top: `${APP_BAR_HEIGHT}px`,
          height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
          borderRadius: 0,
          border: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: workspace.background }}>
        <Box sx={{ flexShrink: 0, px: 2, py: 1.5,
                   bgcolor: workspace.card, borderBottom: `1px solid ${workspace.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesomeIcon fontSize="small" color="primary" />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography id="copilot-title" variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                ASSERTION Copilot
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {scope.project_name || 'Loading project context\u2026'}
              </Typography>
            </Box>
            <Tooltip title="Start a new conversation. Audit history is retained.">
              <span>
                <IconButton size="small" onClick={resetConversation} disabled={messages.length === 0}
                            aria-label="Start a new conversation">
                  <AddCommentOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose} aria-label="Close ASSERTION Copilot">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box ref={scrollRef} onScroll={handleScroll} role="log" aria-live="polite" aria-busy={busy}
             aria-label="Copilot conversation" sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
          {messages.length === 0 && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Ask about this project's consents, delivery risk, regulatory data sources, or how ASSERTION works.
                Answers use only the ASSERTION data you are permitted to see.
              </Typography>
              {(scope.user_name || scope.role) && (
                <Typography variant="caption" color="text.secondary">
                  {[scope.user_name, scope.role].filter(Boolean).join(' \u00b7 ')}
                </Typography>
              )}
              <Stack spacing={1} role="group" aria-label="Suggested questions">
                {suggestions.slice(0, 5).map((item) => (
                  <Chip key={item} label={item} variant="outlined" onClick={() => submit(item)}
                        sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.75, bgcolor: workspace.card,
                              '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' } }} />
                ))}
              </Stack>
            </Stack>
          )}

          <Stack spacing={1.5}>
            {messages.map((message, index) => {
              const isLast = index === messages.length - 1;
              if (message.kind === 'user') {
                return (
                  <Box key={message.id} sx={{ alignSelf: 'flex-end', maxWidth: '85%', ml: 'auto' }}>
                    <Paper sx={{ px: 1.5, py: 1, bgcolor: '#2450C6', color: '#FFFFFF', border: 'none' }}>
                      <Typography variant="body2">{message.text}</Typography>
                    </Paper>
                  </Box>
                );
              }
              if (message.kind === 'error') {
                return (
                  <Paper key={message.id} sx={{ p: 1.5, borderColor: '#FCA5A5' }}>
                    <Typography variant="body2" color="error">{message.text}</Typography>
                  </Paper>
                );
              }
              const { answer } = message;
              const followUps = isLast && !busy ? (answer.follow_ups || []).slice(0, 2) : [];
              return (
                <Paper key={message.id} sx={{ p: 1.75, bgcolor: workspace.card }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{answer.summary}</Typography>
                  {answer.bullets.length > 0 && (
                    <Box component="ul" sx={{ pl: 2.25, mt: 1.25, mb: 0.5, '& li': { mb: 0.75, pl: 0.25 } }}>
                      {answer.bullets.map((bullet, position) => (
                        <Typography key={position} component="li" variant="body2" color="text.secondary"
                                    sx={{ lineHeight: 1.55 }}>{bullet}</Typography>
                      ))}
                    </Box>
                  )}
                  {answer.citations.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.25 }} />
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Sources
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {answer.citations.map((citation) => (
                          <Chip
                            key={`${citation.type}-${citation.reference}`}
                            size="small"
                            icon={CITATION_ICON[citation.type]}
                            label={citation.type === 'consent' ? citation.reference : citation.label}
                            onClick={() => { navigate(citation.route); onClose(); }}
                            aria-label={`Open ${citation.label}`}
                            sx={{ maxWidth: 240 }}
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                    {answer.mode === 'offline' && (
                      <Tooltip title="The assistant is unavailable, so this answer was generated directly from ASSERTION data.">
                        <Chip size="small" icon={<CloudOffIcon fontSize="small" />} label="Offline answer" variant="outlined" />
                      </Tooltip>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Helpful">
                      <span>
                        <IconButton size="small" disabled={!!rated[answer.answer_id]} onClick={() => rate(answer.answer_id, 'helpful')}
                                    aria-label="Mark this answer as helpful">
                          <ThumbUpOffAltIcon fontSize="small" color={rated[answer.answer_id] === 'helpful' ? 'primary' : 'inherit'} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Not helpful">
                      <span>
                        <IconButton size="small" disabled={!!rated[answer.answer_id]} onClick={() => rate(answer.answer_id, 'unhelpful')}
                                    aria-label="Mark this answer as not helpful">
                          <ThumbDownOffAltIcon fontSize="small" color={rated[answer.answer_id] === 'unhelpful' ? 'primary' : 'inherit'} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  {followUps.length > 0 && (
                    <Stack direction="row" spacing={0.5} role="group" aria-label="Follow-up questions"
                           sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {followUps.map((item) => (
                        <Chip key={item} size="small" variant="outlined" label={item} onClick={() => submit(item)}
                              sx={{ maxWidth: '100%', height: 'auto', minHeight: 32, py: 0.5,
                                    bgcolor: workspace.cardSecondary, borderColor: workspace.border,
                                    color: 'text.secondary',
                                    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                                    '&:hover': { bgcolor: workspace.card } }} />
                      ))}
                    </Stack>
                  )}
                </Paper>
              );
            })}
            {busy && (
              <Paper sx={{ p: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">{'Reviewing your ASSERTION data\u2026'}</Typography>
              </Paper>
            )}
          </Stack>
          <div ref={endRef} />
        </Box>

        <Box sx={{ flexShrink: 0, p: 1.5, bgcolor: workspace.card, borderTop: `1px solid ${workspace.border}` }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            inputRef={inputRef}
            placeholder={'Ask about this project\u2026'}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); submit(question); }
            }}
            inputProps={{ maxLength: 500, 'aria-label': 'Ask ASSERTION Copilot a question' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" color="primary" disabled={busy || !question.trim()}
                              onClick={() => submit(question)} aria-label="Send question">
                    <SendIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Grounded in ASSERTION data only. Every question is recorded in the audit log.
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
